import { BookOpen, LogOut, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@/components/common/IconButton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { authApi, useAuthStore } from '@/features/auth';
import { NotificationBell } from '@/features/notifications';
import { HeaderTenantPicker } from '@/features/tenants';
import { APP_NAME, ROUTES } from '@/constants';
import { getInitials } from '@/utils';
import { AppLogo } from './AppLogo';

export function AppHeader() {
	const navigate = useNavigate();
	const user = useAuthStore((state) => state.user);
	const refreshToken = useAuthStore((state) => state.refreshToken);
	const clearSession = useAuthStore((state) => state.clearSession);

	const initials = user ? getInitials(user.email) : '';

	const handleLogout = () => {
		const token = refreshToken;
		clearSession();
		navigate(ROUTES.login, { replace: true });
		// Best-effort server-side revocation; the local session is cleared first.
		if (token) void authApi.logout(token).catch(() => undefined);
	};

	return (
		<header className="bg-background sticky top-0 z-40 flex h-16 items-center justify-between border-b px-4 sm:px-6">
			<div className="flex min-w-0 items-center gap-2">
				<span className="flex items-center gap-2 text-base font-semibold lg:hidden">
					<AppLogo className="size-7" />
					<span className="hidden sm:inline">{APP_NAME}</span>
				</span>
				<HeaderTenantPicker />
			</div>

			<div className="flex items-center gap-1">
				{/* Opens in a new tab so the guide can be read beside the CMS. */}
				<IconButton label="User guide" variant="ghost" asChild>
					<a href={ROUTES.guide} target="_blank" rel="noopener noreferrer">
						<BookOpen />
					</a>
				</IconButton>
				<NotificationBell />
				<ThemeToggle />
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" className="h-auto gap-2 px-2">
							<Avatar className="size-7">
								<AvatarFallback>
									{initials || <User className="size-4" />}
								</AvatarFallback>
							</Avatar>
							<span className="hidden max-w-[160px] truncate text-sm sm:inline">
								{user?.email ?? 'Account'}
							</span>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-48">
						<DropdownMenuLabel className="truncate">
							{user?.email ?? 'Account'}
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuItem variant="destructive" onClick={handleLogout}>
							<LogOut />
							Sign out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</header>
	);
}
